/**
 * HealAI facility-api tenant resolution for OHIF.
 * RIS stores the facility tenant as `clientId` in localStorage (decrypted value).
 * Do NOT confuse with Keycloak OAuth client id (e.g. medplus-healai-client).
 */

const TENANT_STORAGE_KEYS = ['healaiTenantId', 'clientId', 'X-Tenant-Id', 'tenantId'] as const;

const JWT_TENANT_KEYS = ['tenant_id', 'tenantId', 'tenant'] as const;

function readConfigDefaultTenant(): string {
  try {
    const cfg = (window as unknown as {
      config?: {
        healai?: { defaultTenantId?: string; tenantId?: string };
        risDictation?: { tenantId?: string };
      };
    }).config;
    return (
      cfg?.healai?.defaultTenantId?.trim() ||
      cfg?.healai?.tenantId?.trim() ||
      cfg?.risDictation?.tenantId?.trim() ||
      'medplus'
    );
  } catch {
    return 'medplus';
  }
}

function looksLikePlainTenant(value: string): boolean {
  // Skip RIS AES blobs / JWT-looking values
  return value.length > 0 && value.length < 128 && /^[a-zA-Z0-9._-]+$/.test(value);
}

/** Legacy OHIF defaults that must not win over config (e.g. medplus). */
const INVALID_TENANT_VALUES = new Set([
  'local-tenant',
  'local',
  'undefined',
  'null',
  'none',
]);

function isUsableTenant(value: string | null | undefined): value is string {
  if (!value || !looksLikePlainTenant(value)) return false;
  if (INVALID_TENANT_VALUES.has(value.toLowerCase())) return false;
  if (value.includes('healai-client')) return false;
  return true;
}

export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const raw = token.startsWith('Bearer ') ? token.slice(7) : token;
    const part = raw.split('.')[1];
    if (!part) return null;
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

export function tenantFromJwt(token: string | null | undefined): string | null {
  if (!token) return null;
  const payload = decodeJwtPayload(token);
  if (!payload) return null;
  for (const key of JWT_TENANT_KEYS) {
    const v = payload[key];
    if (typeof v === 'string' && isUsableTenant(v.trim())) {
      return v.trim();
    }
  }
  return null;
}

export function tenantFromUrl(search?: string): string | null {
  if (typeof window === 'undefined' && !search) return null;
  try {
    const params = new URLSearchParams(search ?? window.location.search);
    for (const key of ['tenant', 'tenantId']) {
      const v = params.get(key)?.trim();
      if (isUsableTenant(v)) return v;
    }
    // Only accept clientId query when it looks like a tenant (not oauth client names with many hyphens from Keycloak)
    const clientId = params.get('clientId')?.trim();
    if (isUsableTenant(clientId)) return clientId;
  } catch {
    // ignore
  }
  return null;
}

export function tenantFromStorage(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    for (const key of TENANT_STORAGE_KEYS) {
      const v = window.localStorage?.getItem(key)?.trim();
      if (isUsableTenant(v)) return v;
    }
  } catch {
    // ignore
  }
  return null;
}

export function readAccessTokenFromStorage(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const key =
      (window as unknown as { config?: { healai?: { risAccessTokenKey?: string } } }).config?.healai
        ?.risAccessTokenKey || 'accessToken';
    return window.localStorage?.getItem(key)?.trim() || null;
  } catch {
    return null;
  }
}

/** Persist facility tenant for subsequent API calls. */
export function persistFacilityTenant(tenant: string): void {
  if (typeof window === 'undefined' || !tenant.trim()) return;
  const value = tenant.trim();
  try {
    window.localStorage.setItem('healaiTenantId', value);
    // Plain clientId used by facility-api RequestContext (same header name RIS sends)
    window.localStorage.setItem('clientId', value);
  } catch {
    // ignore
  }
}

/**
 * Resolve facility tenant for X-Tenant-Id / clientId headers.
 * Priority: explicit → URL → storage → JWT → app config default.
 * Legacy values like `local-tenant` are ignored so config (medplus) can win.
 */
export function resolveFacilityTenant(explicit?: string | null): string {
  const explicitOk = explicit && isUsableTenant(explicit.trim()) ? explicit.trim() : null;
  return (
    explicitOk ||
    tenantFromUrl() ||
    tenantFromStorage() ||
    tenantFromJwt(readAccessTokenFromStorage()) ||
    readConfigDefaultTenant()
  );
}

/** Headers every facility-api call needs (plus optional Authorization). */
export function facilityTenantHeaders(explicitTenant?: string | null): Record<string, string> {
  const tenant = resolveFacilityTenant(explicitTenant);
  return {
    'X-Tenant-Id': tenant,
    clientId: tenant,
  };
}
